import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { openWhatsApp } from "@/lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_CHIPS = ["Pricing for a caretaker", "Book a GNM nurse", "Japa maid details", "ICU at home"];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Namaste! I'm the Sharpin Care Assistant. Ask me about our caretakers, GNM & B.Sc nurses, baby care, japa maids, pricing — or anything else.",
    },
  ]);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  if (!sessionRef.current) {
    let sid = localStorage.getItem("sharpin_chat_session");
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem("sharpin_chat_session", sid);
    }
    sessionRef.current = sid;
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setInput("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: trimmed }),
      });
      if (!res.ok || !res.body) throw new Error("chat_failed");
      setMessages((m) => [...m, { role: "assistant", content: "" }]);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let failed = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.error) failed = true;
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, content: last.content + payload.delta };
                return copy;
              });
            }
          } catch { /* partial json chunk */ }
        }
      }
      if (failed) throw new Error("assistant_unavailable");
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I'm having trouble connecting right now. Please reach our care team directly on WhatsApp +91-9211949414 — we're available 24/7.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, type: "spring", stiffness: 260, damping: 18 }}
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-btn"
        aria-label="Open Sharpin AI care assistant"
        className="fixed bottom-6 left-6 z-50 h-16 w-16 rounded-full bg-navy text-white flex items-center justify-center shadow-[0_10px_30px_rgba(15,23,42,0.4)] hover:bg-tealbrand transition-colors duration-300"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 left-6 z-50 w-[calc(100vw-3rem)] max-w-sm bg-white rounded-3xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col"
            data-testid="chat-panel"
          >
            <div className="bg-navy px-5 py-4 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-tealbrand/20 flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4 text-tealbrand-light" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-white text-sm">Sharpin Care Assistant</p>
                <p className="text-[11px] text-slate-400">AI-powered · replies instantly</p>
              </div>
              <button
                onClick={() => openWhatsApp("Hello Sharpin Healthcare Services, I'd like to talk to a care coordinator.")}
                data-testid="chat-human-btn"
                className="text-[11px] font-bold uppercase tracking-wider text-tealbrand-light hover:text-white transition-colors shrink-0"
              >
                Human
              </button>
            </div>

            <div ref={scrollRef} className="h-80 overflow-y-auto px-4 py-4 space-y-3 bg-alabaster" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-tealbrand text-white rounded-br-md"
                        : "bg-white border border-slate-200 text-navy rounded-bl-md"
                    }`}
                    data-testid={`chat-message-${m.role}-${i}`}
                  >
                    {m.content}
                    {m.role === "assistant" && busy && i === messages.length - 1 && m.content === "" && (
                      <Loader2 className="h-4 w-4 animate-spin text-tealbrand" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-4 pt-3 pb-2 flex gap-2 overflow-x-auto" data-testid="chat-quick-chips">
              {QUICK_CHIPS.map((chip) => (
                <button
                  key={chip}
                  onClick={() => send(chip)}
                  data-testid={`chat-chip-${chip.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="shrink-0 text-xs font-semibold text-tealbrand-dark bg-tealbrand-light/70 hover:bg-tealbrand-light px-3 py-1.5 rounded-full transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>

            <form
              className="p-4 pt-2 flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="chat-input"
                placeholder="Ask about services, pricing…"
                className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-tealbrand/50"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="chat-send-btn"
                aria-label="Send message"
                className="h-10 w-10 rounded-full bg-navy text-white flex items-center justify-center hover:bg-tealbrand transition-colors disabled:opacity-50 shrink-0"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
